import React, { useCallback, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import CategoryChips, { VIEW_NEW, VIEW_TOP } from '../Components/CategoryChips'
import FeedList from '../Components/FeedList'
import { NewStoriesPill, RefreshRow } from '../Components/FeedStatus'
import { useRail } from '../Components/Layout'
import { Lockup } from '../Components/Logo'
import MainStoryCard from '../Components/MainStoryCard'
import { HOME_REFRESH_EVENT, useHomeTap } from '../Components/Nav'
import { TrendingBlock } from '../Components/Trending'
import { useDocumentTitle } from '../Hooks/useDocumentTitle'
import { useFeed, useNewStories } from '../Hooks/useFeed'
import useTapToRefresh from '../Hooks/useTapToRefresh'
import { t } from '../i18n'
import { api } from '../Services/api'
import { useFollow } from '../State/Follow'
import { useCategories, useTrending } from '../State/refData'
import { useUi } from '../State/Ui'
import { track } from '../Utils/analytics'

/* Početna (spec 5.1): chips filter the feed in place and the URL follows
   (/?kategorija=politika, /?prikaz=najnovije). */
function useView() {
  const [params, setParams] = useSearchParams()
  const view =
    params.get('prikaz') === 'najnovije' ? VIEW_NEW : params.get('kategorija') || VIEW_TOP
  const setView = useCallback(
    (value) => {
      const next =
        value === VIEW_TOP
          ? {}
          : value === VIEW_NEW
          ? { prikaz: 'najnovije' }
          : { kategorija: value }
      setParams(next, { replace: true }) // a new location: the feed starts at the top
    },
    [setParams]
  )
  return [view, setView]
}

export default function Home() {
  const [view, setView] = useView()
  const { isMobile, noRail } = useUi()
  const { sources: followed } = useFollow()
  const homeTap = useHomeTap()
  useDocumentTitle(null)

  const categories = useCategories()
  const isCategory = view !== VIEW_TOP && view !== VIEW_NEW
  const category = isCategory
    ? categories.find((c) => c.slug === view) || { slug: view, title: view }
    : null
  const sort = view === VIEW_NEW ? 'new' : 'top'

  const feed = useFeed(`home:${view}:${followed.join(',')}`, ({ cursor, refresh }) =>
    api.feed({ category: category?.slug, sort, cursor, followed, refresh })
  )
  const { row, tap, run } = useTapToRefresh(feed)
  useRail({ category: category ? { slug: category.slug, title: category.title } : null })

  useEffect(() => {
    track('feed_view', { view })
  }, [view])

  // The pill: only on "Sve" (Najčitanije) and "Najnovije" (spec 5.4).
  const pill = useNewStories({
    enabled: !isCategory && feed.status === 'ready',
    category: null,
    sort,
    asOf: feed.asOf,
    snapshot: feed.snapshot,
    loadedAt: feed.loadedAt,
    onAway: () => {
      window.scrollTo({ top: 0 })
      feed.refresh({ markNew: false })
    },
  })
  const pillShown = useRef(false)
  useEffect(() => {
    if (pill.count > 0 && !pillShown.current)
      track('new_stories_pill_shown', { count: pill.count, view })
    pillShown.current = pill.count > 0
  }, [pill.count, view])

  const onPill = () => {
    track('new_stories_pill_click', { count: pill.count, view })
    window.scrollTo({ top: 0, behavior: 'smooth' })
    feed.refresh({ markNew: true })
  }

  const select = (value) => {
    if (value === view) {
      tap()
      return
    }
    track('category_select', { category: value })
    setView(value)
  }

  // Početna / logo tapped at the top: back to "Sve", refreshed (spec 6).
  const refreshAfterSwitch = useRef(false)
  useEffect(() => {
    const onHomeRefresh = () => {
      if (view === VIEW_TOP) run()
      else {
        refreshAfterSwitch.current = true
        setView(VIEW_TOP)
      }
    }
    window.addEventListener(HOME_REFRESH_EVENT, onHomeRefresh)
    return () => window.removeEventListener(HOME_REFRESH_EVENT, onHomeRefresh)
  }, [view, run, setView])
  useEffect(() => {
    if (view === VIEW_TOP && feed.status === 'ready' && refreshAfterSwitch.current) {
      refreshAfterSwitch.current = false
      run()
    }
  }, [view, feed.status, run])

  // Aktuelne teme in the feed wherever the right column isn't shown (phones
  // and windows under 1100 px): under the Glavna vijest on Najčitanije, and on
  // a category chip under its Glavna vijest (spec 7.3). Never on Najnovije,
  // the plain newest-first list (spec 5.1).
  const trending = useTrending({ limit: 5, enabled: noRail && view === VIEW_TOP })
  const categoryTrending = useTrending({
    category: category?.slug,
    limit: 5,
    enabled: noRail && isCategory,
  })
  let block = null
  if (noRail) {
    if (view === VIEW_TOP) block = <TrendingBlock topics={trending.topics} />
    else if (isCategory && feed.mainStory && categoryTrending.topics.length >= 2) {
      block = (
        <TrendingBlock
          topics={categoryTrending.topics}
          title={t('trending.categoryTitle', { category: category.title })}
        />
      )
    }
  }

  return (
    <div className="home">
      <h1 className="sr-only">{t('meta.homeHeading')}</h1>
      {isMobile && (
        <div className="home-header">
          <Link to="/" aria-label={t('common.logoLabel')} onClick={homeTap}>
            <Lockup icon={34} wordmark={25} />
          </Link>
        </div>
      )}
      <div className="sticky-bar">
        <CategoryChips value={view} onSelect={select} />
      </div>
      <NewStoriesPill count={row ? 0 : pill.count} sources={pill.sources} onClick={onPill} />
      <RefreshRow state={row} />

      {feed.status === 'ready' && feed.mainStory && (
        <div className="feed-item">
          <MainStoryCard
            story={feed.mainStory}
            isNew={feed.newIds.has(feed.mainStory.id)}
            from="home_main"
          />
        </div>
      )}
      {feed.status === 'ready' && block}
      <FeedList ads={false} feed={feed} from="home" skeletonMain={sort === 'top' && !isCategory} />
    </div>
  )
}
